import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import { useMeasure } from 'react-use'
import { QuestionTemplate, QuestionType } from '../../../model/Questionnaire'
import { MoreButton } from '../../shared'
import { QuestionContext } from '../context'
import {
  AddSubQuestionCallback,
  ChangeQuestionImportanceCallback,
  DeleteQuestionCallback,
  EditQuestionCallback,
  QuestionnaireMode,
  SetAnswerValueCallback,
  ShowSubQuestionsCallback,
} from '../Questionnaire.model'
import { QuestionDetail } from './QuestionDetail'
import styles from './SubQuestions.module.scss'
import { isDefined } from '../../../misc/functions.utilities'

function displaySubQuestionLabel(showSubQuestions: boolean, subQuestionsTotal: number) {
  const questionNumberLabel =
    subQuestionsTotal > 1 ? `les ${subQuestionsTotal} sous questions` : 'la sous question'
  if (showSubQuestions) {
    return subQuestionsTotal > 0 ? `Masquer ${questionNumberLabel}` : 'Masquer les sous questions'
  } else {
    return subQuestionsTotal > 0 ? `Voir ${questionNumberLabel}` : 'Ajouter une sous question'
  }
}

interface SubQuestionsProps {
  mode: QuestionnaireMode
  questionTemplate: QuestionTemplate
  onAddSubQuestion?: AddSubQuestionCallback
  onDelete?: DeleteQuestionCallback
  onEdit?: EditQuestionCallback
  onChangeQuestionImportance?: ChangeQuestionImportanceCallback
  setAnswerValue?: SetAnswerValueCallback
  answerValuesIds?: number[] | null
  subQuestionsShown?: boolean
  onShowSubQuestions?: ShowSubQuestionsCallback
  disabled?: boolean
  isReadonly?: boolean
}

export const SubQuestions: FunctionComponent<SubQuestionsProps> = ({
  mode,
  questionTemplate,
  subQuestionsShown = false,
  onEdit,
  onChangeQuestionImportance,
  onDelete,
  onAddSubQuestion,
  onShowSubQuestions,
  answerValuesIds,
  disabled,
  isReadonly,
  setAnswerValue,
}) => {
  const [overflowHidden, setOverflowHidden] = useState(true)
  const { scrollToAnswerId } = useContext(QuestionContext)

  /**
   * Affichage des sous questions
   *  - ce n'est pas une sous question
   *  - s'il est demandé de scroller dans les sous questions "scrollToAnswerId"
   *  - s'il en existe déjà
   */
  const displaySubQuestions = useMemo(
    () =>
      (!questionTemplate.parentAnswerId &&
        scrollToAnswerId &&
        questionTemplate.answers.map(({ id }) => id).includes(scrollToAnswerId)) ||
      questionTemplate.answers.some((answer) =>
        answer.childQuestions ? answer.childQuestions.length > 0 : false,
      ),
    [questionTemplate.parentAnswerId, scrollToAnswerId, questionTemplate.answers],
  )

  const handleClickShowingMore = () => {
    const show = !subQuestionsShown
    setOverflowHidden(true) // il faut avoir le overflow hidden durant la transition
    if (onShowSubQuestions) {
      onShowSubQuestions(show)
    }
  }

  const answers = questionTemplate.answers.filter((answer) => {
    if (answerValuesIds !== undefined) {
      return (
        answerValuesIds &&
        answerValuesIds.includes(answer.id) &&
        answer.childQuestions &&
        answer.childQuestions.length > 0
      )
    }
    return true
  })

  const hasChildQuestions = useMemo(
    () =>
      answers.some((answer) => (answer.childQuestions ? answer.childQuestions.length > 0 : false)),
    [answers],
  )
  const subQuestionsTotal = useMemo(() => {
    return answers.reduce(
      (acc, answer) => (acc += answer.childQuestions ? answer.childQuestions.length : 0),
      0,
    )
  }, [answers])
  useEffect(() => {
    if ((hasChildQuestions || displaySubQuestions) && onShowSubQuestions) {
      onShowSubQuestions(true)
    }
  }, [hasChildQuestions, onShowSubQuestions, displaySubQuestions])

  const [ref, { height }] = useMeasure()

  const style = useSpring({
    to: { height: subQuestionsShown ? height : 0 },
    onRest: () => {
      setOverflowHidden(!subQuestionsShown)
    },
  })

  // Condition pour afficher le boutton d'affichage des sous questions
  const isMoreButtonDisplayed = useMemo(() => {
    const hasSubQuestions = questionTemplate.answers.length > 0 && subQuestionsTotal > 0
    const canBeAddedSubQuestions =
      mode === QuestionnaireMode.managerEdit &&
      ![QuestionType.Slider, QuestionType.Text, QuestionType.Date].includes(
        questionTemplate.type,
      ) &&
      questionTemplate.answers.length > 0
    const isNotSubQuestion = !isDefined(questionTemplate.parentAnswerId)

    return hasSubQuestions || (isNotSubQuestion && canBeAddedSubQuestions)
  }, [
    mode,
    questionTemplate.answers.length,
    questionTemplate.parentAnswerId,
    questionTemplate.type,
    subQuestionsTotal,
  ])

  return (
    <>
      {isMoreButtonDisplayed ? (
        <div className={styles.subQuestionsButtonContainer}>
          <MoreButton showingMore={subQuestionsShown} onChange={() => handleClickShowingMore()}>
            {displaySubQuestionLabel(subQuestionsShown, subQuestionsTotal)}
          </MoreButton>
        </div>
      ) : null}
      {(answers.length > 0 || mode === QuestionnaireMode.managerEdit) && (
        <animated.div style={{ ...style, overflow: overflowHidden ? 'hidden' : 'visible' }}>
          <div ref={ref}>
            <QuestionDetail
              questionTitle={questionTemplate.title}
              mode={mode}
              answers={mode === QuestionnaireMode.fill ? answers : questionTemplate.answers}
              setAnswerValue={setAnswerValue}
              disabled={!!disabled}
              onAddSubQuestion={isReadonly ? undefined : onAddSubQuestion}
              onDelete={onDelete}
              onEdit={onEdit}
              onChangeQuestionImportance={onChangeQuestionImportance}
              isReadonly={isReadonly}
            ></QuestionDetail>
          </div>
        </animated.div>
      )}
    </>
  )
}
